/** Sync with Service Worker */

import { logger } from '@tunasong/models'
import { APIMap, useThunkDispatch, type APIName } from '@tunasong/redux'
import { useEffect } from 'react'
import invariant from 'tiny-invariant'
import type { SWSyncMessage } from './message.js'

export const useSWSync = () => {
  const dispatch = useThunkDispatch()

  useEffect(() => {
    const serviceWorker = navigator?.serviceWorker
    if (!(serviceWorker && 'caches' in window)) {
      return
    }

    const handleSWMessage = async (msg: MessageEvent<{ payload: SWSyncMessage }>) => {
      try {
        const { cacheName, responseType, updatedURL, rtkQueryArgs, rtkQueryEndpointName, rtkQueryApiName } =
          msg.data.payload

        logger.debug(`Service Worker Sync: ${cacheName} - ${updatedURL}`, {
          responseType,
          rtkQueryEndpointName,
          rtkQueryArgs,
          rtkQueryApiName,
        })

        /**
         * Not all cached responses belong to a query: the image route broadcasts static images such as /favicon.png
         * too. Expected, so debug rather than warn (GH #2663).
         */
        if (!(rtkQueryArgs && rtkQueryEndpointName)) {
          logger.debug(`Service Worker Sync: no RTK Query for ${cacheName} - ${updatedURL}`, {
            rtkQueryApiName,
            rtkQueryArgs,
            rtkQueryEndpointName,
          })
          return
        }

        /**
         * Match the data from the cache
         * @todo does this match cases where SW strategies has rewritten the cache key?
         */
        const data = await caches.match(updatedURL, { cacheName })
        if (!data) {
          logger.warn(`Service Worker Sync: Cache entry not found: ${cacheName} - ${updatedURL}`)
          return
        }

        // Map to API
        const api = APIMap[rtkQueryApiName as APIName]
        invariant(api, `Service Worker Sync: API not found: ${rtkQueryApiName}`)

        // Load the data from the cache. It may be a blob or a JSON
        const blob = await data.blob()
        let payload: Record<string, unknown> | Uint8Array

        logger.debug(`Service Worker Sync: ${blob.type} data found in cache: ${cacheName} - ${updatedURL}`)
        if (blob.type === 'application/json') {
          payload = JSON.parse(await blob.text()) as Record<string, unknown>
        } else {
          payload = new Uint8Array(await blob.arrayBuffer())
        }

        const args = JSON.parse(decodeURIComponent(rtkQueryArgs))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const upsertQueryData = api.util.upsertQueryData as (endpointName: string, args: any, data: any) => any
        logger.debug(`Service Worker Sync: Updating RTK Query data: ${rtkQueryApiName} - ${rtkQueryEndpointName}`, {
          args,
          data,
        })
        dispatch(upsertQueryData(rtkQueryEndpointName, args, payload))
      } catch (err) {
        // iOS Safari frequently aborts cache/IDB I/O when backgrounding tabs.
        // Log as warn so transient environmental failures don't spam Sentry.
        logger.warn('Service Worker Sync: handler failed', err)
      }
    }

    serviceWorker.addEventListener('message', handleSWMessage)
    return () => {
      serviceWorker.removeEventListener('message', handleSWMessage)
    }
  }, [dispatch])
}
